import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * Scope users — the assignee directory for cases.
 *
 *     POST /{clusterId}/api/users/list   { scope_id } -> users
 *
 * VERIFIED 2026-08-24 against ap1/gotham (docs/inventory/
 * cases-write-capture.json): standard `{ TStamp, count, data }` envelope,
 * rows `{ user_id, name, email_id, role, user_type, org_name,
 * is_cluster_admin, … }`. `email_id` matters beyond display: cases/add_note
 * requires its `author` to be one of these emails.
 */

export const orgUser = z
  .object({
    user_id: z.string(),
    // UAT (2026-10-14) answers null for some users' email, org and admin flag: read null as unset.
    name: z.string().nullish().transform((v) => v ?? ''),
    email_id: z.string().nullish().transform((v) => v ?? ''),
    role: z.string().nullish().transform((v) => v ?? ''),
    user_type: z.string().nullish().transform((v) => v ?? ''),
    org_name: z.string().nullish().transform((v) => v ?? ''),
    is_cluster_admin: z.boolean().nullish().transform((v) => v ?? false),
  })
  .passthrough()
export type OrgUser = z.output<typeof orgUser>

export const usersListResponse = z
  .object({
    status: z.string().optional(),
    count: z.number().optional(),
    data: z.array(orgUser).default([]),
  })
  .transform((r) => r.data)

export interface UsersApi {
  list: (q: { clusterId: string; scopeId: string }) => Promise<OrgUser[]>
}

export function createUsersApi(client: ApiClient): UsersApi {
  return {
    list: async ({ clusterId, scopeId }) =>
      client.request('POST', `/${clusterId}/api/users/list`, {
        body: { scope_id: scopeId },
        schema: usersListResponse,
      }),
  }
}
